// src/components/cart/CheckoutPageClient.tsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { apiPath } from "@/lib/apiPath";
import Section from "../ui/Section";
import Wrapper from "../ui/Wrapper";
import { Button } from "../ui/Button";
import { Input } from "@/components/ui/Input";
import Checkbox from "@/components/ui/Checkbox";
import SafeHtml from "../ui/SafeHtml";
import OtpInput from "@/components/ui/OtpInput";

import { useCart } from "./cart-context";
import { useLayoutData } from "@/components/layout/LayoutDataProvider";
import api from "@/lib/auth/apiClient";
import { CountrySelect } from "../ui/CountrySelect";
import CheckoutSidebar from "./CheckoutSidebar";
import CheckoutSuccessView from "./CheckoutSuccessView";
import {
  EMPTY_ADDR,
  coerceCart,
  extractOrderRef,
  getMoneyPrimitive,
  looksLikeEmail,
  toInt,
  resolveLocaleFromLang,
  type AddressForm,
  type Cart,
  type CartItem,
  type Coupon,
  type Me,
  type PaymentMethod,
  type Props,
  type SuccessOrder,
} from "@/lib/checkoutHelpers";

const OTP_LEN = 6;
const RESEND_COOLDOWN_SECONDS = 20;

type TFn = (k: string, fallback?: string) => string;

// ---- namespace hooks ---------------------------------------------------

function useCheckoutT(): TFn {
  const { global_text } = useLayoutData();
  const table =
    (global_text as { cart?: { checkout?: Record<string, string> } } | undefined)
      ?.cart?.checkout ?? {};
  return (k, fallback = "") => table[k] || fallback;
}

function useAuthLoginT(): TFn {
  const { global_text } = useLayoutData();
  const table =
    (global_text as { auth?: { login?: Record<string, string> } } | undefined)
      ?.auth?.login ?? {};
  return (k, fallback = "") => table[k] || fallback;
}

function useAuthSignupT(): TFn {
  const { global_text } = useLayoutData();
  const table =
    (global_text as { auth?: { signup?: Record<string, string> } } | undefined)
      ?.auth?.signup ?? {};
  return (k, fallback = "") => table[k] || fallback;
}

// ---- helpers -----------------------------------------------------------

function decodeHtmlEntities(input: string) {
  const txt = document.createElement("textarea");
  txt.innerHTML = input;
  return txt.value;
}

async function getNiceErrorMessage(res: Response, fallbackTemplate: string) {
  const text = await res.text().catch(() => "");
  try {
    const json = JSON.parse(text) as { message?: unknown };
    if (typeof json.message === "string" && json.message.trim()) {
      return decodeHtmlEntities(json.message);
    }
  } catch {
    /* ignore */
  }
  return text ? text.slice(0, 300) : fallbackTemplate.replace("{status}", String(res.status));
}

function scrollToEl(el: HTMLElement | null, offset = 96) {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
}

function formatCartTotalsMoney(
  totals: Cart["totals"] | null | undefined,
  rawValue: unknown,
  locale: string
): string | null {
  if (!totals) return null;

  function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === "object" && v !== null;
  }

  function hasCurrency(s: string) {
    return /[€$£¥]|eur|usd|gbp/i.test(s);
  }

  function primitive(v: unknown): string | number | null {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number" && Number.isFinite(v)) return v;

    if (isRecord(v)) {
      const raw = v.value;
      if (typeof raw === "string" && raw.trim()) return raw.trim();
      if (typeof raw === "number" && Number.isFinite(raw)) return raw;

      const fmt = v.formatted;
      if (typeof fmt === "string" && fmt.trim()) return fmt.trim();
    }

    return null;
  }

  const p = primitive(rawValue);
  if (p === null) return null;
  if (typeof p === "string" && hasCurrency(p)) return p;

  const code = totals.currency_code;
  const unit = totals.currency_minor_unit;

  if (!code || typeof unit !== "number") return String(p);

  const numeric = typeof p === "string" ? Number(p) : p;
  if (!Number.isFinite(numeric)) return "—";

  const major = numeric / Math.pow(10, unit);

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: code,
    minimumFractionDigits: unit,
    maximumFractionDigits: unit,
  }).format(major);
}

function CheckoutErrorBanner({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <div className="mb-6 overflow-hidden">
      <div className="border-t-4 border-fuji-red bg-fuji-grey px-5 py-4">
        <div className="font-bold">{title}</div>
        <div className="mt-1 text-sm opacity-80">
          <SafeHtml content={message} />
        </div>
      </div>
    </div>
  );
}

// ---- Inline login (reuses auth.login) ----------------------------------

type InlineLoginProps = {
  prefillEmail: string;
  onAuthed: () => void;
};

function InlineLogin({ prefillEmail, onAuthed }: InlineLoginProps) {
  const t = useAuthLoginT();
  const { templateRoutes } = useLayoutData();
  const forgotHref = (templateRoutes ?? {})["page-forgot-password.php"];

  const [step, setStep] = useState<"creds" | "code">("creds");
  const [email, setEmail] = useState(prefillEmail || "");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [otp, setOtp] = useState<string[]>(Array(OTP_LEN).fill(""));
  const [loading, setLoading] = useState(false);
  const [topMessage, setTopMessage] = useState<string | null>(null);
  const [topMessageType, setTopMessageType] = useState<"success" | "error" | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const [resendLeft, setResendLeft] = useState(0);
  const resendTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const next = (prefillEmail ?? "").trim();
    if (next) setEmail((p) => (p.trim() ? p : next));
  }, [prefillEmail]);

  useEffect(() => {
    if (emailError) emailRef.current?.focus?.();
    else if (passwordError) passwordRef.current?.focus?.();
  }, [emailError, passwordError]);

  useEffect(
    () => () => {
      if (resendTimerRef.current) window.clearInterval(resendTimerRef.current);
    },
    []
  );

  function startCooldown(s = RESEND_COOLDOWN_SECONDS) {
    if (resendTimerRef.current) window.clearInterval(resendTimerRef.current);

    setResendLeft(s);

    resendTimerRef.current = window.setInterval(() => {
      setResendLeft((p) => {
        if (p <= 1) {
          window.clearInterval(resendTimerRef.current!);
          resendTimerRef.current = null;
          return 0;
        }

        return p - 1;
      });
    }, 1000);
  }

  function resetMessages() {
    setTopMessage(null);
    setTopMessageType(null);
    setEmailError(null);
    setPasswordError(null);
    setCodeError(null);
  }

  function clearOtp() {
    setOtp(Array(OTP_LEN).fill(""));
  }

  async function callLoginStart() {
    const res = await fetch(apiPath("/api/auth/login/start"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });

    return { res, json: await res.json().catch(() => ({})) };
  }

  async function handleStart(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();

    let err = false;

    if (!email.trim()) {
      setEmailError(t("error_email_required", "Please enter your email."));
      err = true;
    }

    if (!password) {
      setPasswordError(t("error_password_required", "Please enter your password."));
      err = true;
    }

    if (err) return;

    setLoading(true);

    try {
      const { res, json } = await callLoginStart();

      if (!res.ok) {
        setTopMessageType("error");
        setTopMessage(json?.message ?? t("error_login_failed", "Sign in failed."));
        setLoading(false);
        return;
      }

      setChallengeId(json.challengeId);
      clearOtp();
      setStep("code");
      setTopMessage(null);
      startCooldown();
      setLoading(false);
    } catch {
      setTopMessageType("error");
      setTopMessage(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  async function handleResend() {
    resetMessages();

    if (resendLeft > 0) return;

    if (!email.trim() || !password) {
      if (!email.trim()) setEmailError(t("error_email_required", "Please enter your email."));
      if (!password) setPasswordError(t("error_password_required", "Please enter your password."));
      setStep("creds");
      return;
    }

    setLoading(true);

    try {
      const { res, json } = await callLoginStart();

      if (!res.ok) {
        setTopMessageType("error");
        setTopMessage(json?.message ?? t("error_resend_failed", "Could not resend code."));
        setLoading(false);
        return;
      }

      setChallengeId(json.challengeId);
      clearOtp();
      setStep("code");
      setTopMessageType("success");
      setTopMessage(t("resend_success", "Code resent. Check your inbox."));
      startCooldown();
      setLoading(false);
    } catch {
      setTopMessageType("error");
      setTopMessage(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();

    const code = otp.join("").replace(/\D/g, "");

    if (code.length !== OTP_LEN || otp.some((d) => d === "")) {
      setCodeError(t("error_code_incomplete", "Enter the 6-digit code."));
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(apiPath("/api/auth/login/verify"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        credentials: "include",
        body: JSON.stringify({ challengeId, code }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setTopMessageType("error");
        setTopMessage(json?.message ?? t("error_verify_failed", "Verification failed."));
        setLoading(false);
        return;
      }

      try {
        onAuthed();
      } catch { }

      setLoading(false);
    } catch {
      setTopMessageType("error");
      setTopMessage(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  const resendBase = t("resend_button", "Resend code");
  const resendLabel = resendLeft > 0 ? `${resendBase} (${resendLeft}s)` : resendBase;

  const bottomLinks = (
    <div className="text-body-2">
      {forgotHref ? (
        <Link className="underline" href={forgotHref}>
          {t("forgot_link", "Forgot password?")}
        </Link>
      ) : null}
    </div>
  );

  if (step === "creds") {
    return (
      <form onSubmit={handleStart} noValidate className="flex flex-col gap-6">
        <div className="w-full min-w-0">
          <Input
            id="co-login-email"
            ref={emailRef}
            label={<SafeHtml content={t("email_label", "Email")} />}
            type="email"
            value={email}
            onChange={(e) => {
              setEmailError(null);
              setEmail(e.target.value);
            }}
            autoComplete="email"
            aria-invalid={!!emailError || undefined}
            aria-describedby={emailError ? "co-login-email-error" : undefined}
            invalid={!!emailError}
            required
          />

          {emailError && (
            <small
              id="co-login-email-error"
              role="alert"
              className="mt-1 block text-body-3 text-fuji-red"
            >
              {emailError}
            </small>
          )}
        </div>

        <div className="w-full min-w-0">
          <Input
            id="co-login-password"
            ref={passwordRef}
            label={<SafeHtml content={t("password_label", "Password")} />}
            type="password"
            value={password}
            onChange={(e) => {
              setPasswordError(null);
              setPassword(e.target.value);
            }}
            autoComplete="current-password"
            aria-invalid={!!passwordError || undefined}
            aria-describedby={
              passwordError ? "co-login-password-error" : undefined
            }
            invalid={!!passwordError}
            required
          />

          {passwordError && (
            <small
              id="co-login-password-error"
              role="alert"
              className="mt-1 block text-body-3 text-fuji-red"
            >
              {passwordError}
            </small>
          )}
        </div>

        {topMessage && (
          <p
            className={`m-0 text-body-2 ${topMessageType === "error" ? "text-fuji-red" : ""
              }`}
          >
            {topMessage}
          </p>
        )}

        <div className="flex w-full flex-col gap-3">
          <Button
            type="submit"
            className="group w-full"
            disabled={loading}
            loading={loading}
            center
          >
            <SafeHtml content={t("submit_button", "Sign in")} />
          </Button>

          {bottomLinks}
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleVerify} noValidate className="flex flex-col gap-6">
      <p className="text-body-2">
        <SafeHtml
          content={t("otp_sent_to_html", "Enter the 6-digit code sent to <strong>{email}</strong>.").replace("{email}", email)}
        />
      </p>

      <OtpInput
        idPrefix="co-login-otp"
        label={t("otp_label", "Verification code")}
        value={otp}
        onChange={setOtp}
        invalid={!!codeError}
        error={codeError}
        onClearError={() => setCodeError(null)}
      />

      {topMessage && (
        <p
          className={`m-0 text-body-2 ${topMessageType === "error" ? "text-fuji-red" : ""
            }`}
        >
          {topMessage}
        </p>
      )}

      <div className="flex w-full flex-col gap-3">
        <Button
          type="submit"
          className="group w-full"
          disabled={loading}
          loading={loading}
          center
        >
          <SafeHtml content={t("verify_button", "Verify")} />
        </Button>

        <div className="flex items-center justify-between text-body-2">
          <button
            type="button"
            className={`underline ${loading ? "opacity-60 cursor-not-allowed" : ""
              }`}
            onClick={() => {
              if (loading) return;
              resetMessages();
              clearOtp();
              setStep("creds");
            }}
            disabled={loading}
          >
            {t("back_button", "Back")}
          </button>

          <button
            type="button"
            className={`${loading || resendLeft > 0
              ? "opacity-60 cursor-not-allowed"
              : "underline"
              }`}
            onClick={handleResend}
            disabled={loading || resendLeft > 0}
          >
            {resendLabel}
          </button>
        </div>

        {bottomLinks}
      </div>
    </form>
  );
}

// ---- Inline signup (reuses auth.signup + cart.checkout for switcher) ---

type InlineSignupProps = {
  prefillEmail: string;
  onAuthed: () => void;
  onSwitchToLogin: () => void;
  lang: string;
};

function InlineSignup({
  prefillEmail,
  onAuthed,
  onSwitchToLogin,
  lang,
}: InlineSignupProps) {
  const t = useAuthSignupT();
  const tCheckout = useCheckoutT();
  const { global_text } = useLayoutData();

  const termsLegal = global_text?.contact_form?.terms_legal?.trim() ?? "";
  const newsletterLegal =
    global_text?.contact_form?.newsletter_legal?.trim() ?? "";

  const [step, setStep] = useState<"start" | "verify">("start");
  const [email, setEmail] = useState(prefillEmail || "");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [terms, setTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [signupId, setSignupId] = useState("");
  const [otp, setOtp] = useState<string[]>(Array(OTP_LEN).fill(""));
  const [loading, setLoading] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [pw1Error, setPw1Error] = useState<string | null>(null);
  const [pw2Error, setPw2Error] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  const firstNameRef = useRef<HTMLInputElement | null>(null);
  const lastNameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const pw1Ref = useRef<HTMLInputElement | null>(null);
  const pw2Ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const next = (prefillEmail ?? "").trim();
    if (next) setEmail((p) => (p.trim() ? p : next));
  }, [prefillEmail]);

  useEffect(() => {
    if (firstNameError) firstNameRef.current?.focus?.();
    else if (lastNameError) lastNameRef.current?.focus?.();
    else if (emailError) emailRef.current?.focus?.();
    else if (pw1Error) pw1Ref.current?.focus?.();
    else if (pw2Error) pw2Ref.current?.focus?.();
  }, [firstNameError, lastNameError, emailError, pw1Error, pw2Error]);

  function resetMessages() {
    setTopError(null);
    setFirstNameError(null);
    setLastNameError(null);
    setEmailError(null);
    setPw1Error(null);
    setPw2Error(null);
    setTermsError(null);
    setCodeError(null);
  }

  function clearOtp() {
    setOtp(Array(OTP_LEN).fill(""));
  }

  function validateStart() {
    let ok = true;

    if (!firstName.trim()) {
      setFirstNameError(t("error_first_name_required", "Please enter your first name."));
      ok = false;
    }

    if (!lastName.trim()) {
      setLastNameError(t("error_last_name_required", "Please enter your last name."));
      ok = false;
    }

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setEmailError(t("error_email_required", "Please enter your email."));
      ok = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError(t("error_email_invalid", "Please enter a valid email address."));
      ok = false;
    }

    if (!pw1) {
      setPw1Error(t("error_password_required", "Please enter a password."));
      ok = false;
    } else if (pw1.length < 8) {
      setPw1Error(t("error_password_too_short", "Password must be at least 8 characters."));
      ok = false;
    }

    if (!pw2) {
      setPw2Error(t("error_confirm_password_required", "Please confirm your password."));
      ok = false;
    } else if (pw1 && pw1 !== pw2) {
      setPw2Error(t("error_passwords_mismatch", "Passwords do not match."));
      ok = false;
    }

    if (termsLegal && !terms) {
      setTermsError(t("error_terms_required", "You must accept the terms to create an account."));
      ok = false;
    }

    return ok;
  }

  async function handleStart(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();

    if (!validateStart()) return;

    setLoading(true);

    try {
      const res = await fetch(apiPath("/api/auth/register/start"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        credentials: "include",
        body: JSON.stringify({
          email: email.trim(),
          password: pw1,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          display_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
          terms,
          newsletter,
          lang,
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setTopError(json?.message ?? t("error_register_start_failed", "Could not start registration."));
        setLoading(false);
        return;
      }

      setSignupId(json.signupId);
      clearOtp();
      setStep("verify");
      setLoading(false);
    } catch {
      setTopError(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();

    if (!signupId) {
      setTopError(t("error_signup_session_missing", "Registration session missing. Please start over."));
      return;
    }

    const code = otp.join("");

    if (code.length !== OTP_LEN || otp.some((d) => d === "")) {
      setCodeError(t("error_code_incomplete", "Enter the 6-digit code."));
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(apiPath("/api/auth/register/verify"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        credentials: "include",
        body: JSON.stringify({ signupId, code }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setTopError(json?.message ?? t("error_code_invalid", "That code didn't work. Try again."));
        setLoading(false);
        return;
      }

      // Fire-and-forget E-goi subscription — mirrors the footer contact form pattern.
      // Runs only after successful OTP verification so abandoned signups don't get subscribed.
      // Silent catch so an E-goi outage never blocks the checkout flow.
      if (newsletter && email.trim()) {
        void fetch(apiPath("/api/egoi/subscribe"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            name: `${firstName.trim()} ${lastName.trim()}`.trim(),
            lang,
          }),
        }).catch(() => { });
      }

      try {
        onAuthed();
      } catch { }

      setLoading(false);
    } catch {
      setTopError(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  if (step === "start") {
    return (
      <form onSubmit={handleStart} noValidate className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="w-full min-w-0">
            <Input
              id="co-signup-first-name"
              ref={firstNameRef}
              label={<SafeHtml content={t("first_name_label", "First name")} />}
              type="text"
              value={firstName}
              onChange={(e) => {
                setFirstNameError(null);
                setFirstName(e.target.value);
              }}
              autoComplete="given-name"
              aria-invalid={!!firstNameError || undefined}
              aria-describedby={
                firstNameError ? "co-signup-first-name-error" : undefined
              }
              invalid={!!firstNameError}
              required
            />

            {firstNameError && (
              <small
                id="co-signup-first-name-error"
                role="alert"
                className="mt-1 block text-body-3 text-fuji-red"
              >
                {firstNameError}
              </small>
            )}
          </div>

          <div className="w-full min-w-0">
            <Input
              id="co-signup-last-name"
              ref={lastNameRef}
              label={<SafeHtml content={t("last_name_label", "Last name")} />}
              type="text"
              value={lastName}
              onChange={(e) => {
                setLastNameError(null);
                setLastName(e.target.value);
              }}
              autoComplete="family-name"
              aria-invalid={!!lastNameError || undefined}
              aria-describedby={
                lastNameError ? "co-signup-last-name-error" : undefined
              }
              invalid={!!lastNameError}
              required
            />

            {lastNameError && (
              <small
                id="co-signup-last-name-error"
                role="alert"
                className="mt-1 block text-body-3 text-fuji-red"
              >
                {lastNameError}
              </small>
            )}
          </div>
        </div>

        <div className="w-full min-w-0">
          <Input
            id="co-signup-email"
            ref={emailRef}
            label={<SafeHtml content={t("email_label", "Email")} />}
            type="email"
            value={email}
            onChange={(e) => {
              setEmailError(null);
              setEmail(e.target.value);
            }}
            autoComplete="email"
            aria-invalid={!!emailError || undefined}
            aria-describedby={emailError ? "co-signup-email-error" : undefined}
            invalid={!!emailError}
            required
          />

          {emailError && (
            <small
              id="co-signup-email-error"
              role="alert"
              className="mt-1 block text-body-3 text-fuji-red"
            >
              {emailError}
            </small>
          )}
        </div>

        <div className="w-full min-w-0">
          <Input
            id="co-signup-password"
            ref={pw1Ref}
            label={<SafeHtml content={t("password_label", "Password")} />}
            type="password"
            value={pw1}
            onChange={(e) => {
              setPw1Error(null);
              setPw1(e.target.value);
            }}
            autoComplete="new-password"
            aria-invalid={!!pw1Error || undefined}
            aria-describedby={pw1Error ? "co-signup-pw1-error" : undefined}
            invalid={!!pw1Error}
            required
          />

          {pw1Error && (
            <small
              id="co-signup-pw1-error"
              role="alert"
              className="mt-1 block text-body-3 text-fuji-red"
            >
              {pw1Error}
            </small>
          )}
        </div>

        <div className="w-full min-w-0">
          <Input
            id="co-signup-password-confirm"
            ref={pw2Ref}
            label={<SafeHtml content={t("confirm_password_label", "Confirm password")} />}
            type="password"
            value={pw2}
            onChange={(e) => {
              setPw2Error(null);
              setPw2(e.target.value);
            }}
            autoComplete="new-password"
            aria-invalid={!!pw2Error || undefined}
            aria-describedby={pw2Error ? "co-signup-pw2-error" : undefined}
            invalid={!!pw2Error}
            required
          />

          {pw2Error && (
            <small
              id="co-signup-pw2-error"
              role="alert"
              className="mt-1 block text-body-3 text-fuji-red"
            >
              {pw2Error}
            </small>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {termsLegal && (
            <div className="w-full min-w-0">
              <Checkbox
                id="co-signup-terms"
                name="terms"
                checked={terms}
                onChange={(e) => {
                  setTermsError(null);
                  setTerms(e.target.checked);
                }}
                label={<SafeHtml content={termsLegal} />}
              />

              {termsError && (
                <small
                  id="co-signup-terms-error"
                  role="alert"
                  className="mt-1 block text-body-3 text-fuji-red"
                >
                  {termsError}
                </small>
              )}
            </div>
          )}

          {newsletterLegal && (
            <div className="w-full min-w-0">
              <Checkbox
                id="co-signup-newsletter"
                name="newsletter"
                checked={newsletter}
                onChange={(e) => setNewsletter(e.target.checked)}
                label={<SafeHtml content={newsletterLegal} />}
              />
            </div>
          )}
        </div>

        {topError && <p className="m-0 text-body-2 text-fuji-red">{topError}</p>}

        <div className="flex w-full flex-col gap-3">
          <Button
            type="submit"
            className="group w-full"
            disabled={loading}
            loading={loading}
            center
          >
            <SafeHtml content={tCheckout("switch_to_signup", "Register now")} />
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleVerify} noValidate className="flex flex-col gap-6">
      <p className="text-body-2">
        <SafeHtml
          content={t("otp_sent_to_html", "Enter the 6-digit code sent to <strong>{email}</strong>.").replace("{email}", email)}
        />
      </p>

      <OtpInput
        idPrefix="co-signup-otp"
        label={t("otp_label", "Verification code")}
        length={OTP_LEN}
        value={otp}
        onChange={setOtp}
        invalid={!!codeError}
        error={codeError}
        onClearError={() => setCodeError(null)}
      />

      {topError && <p className="m-0 text-body-2 text-fuji-red">{topError}</p>}

      <div className="flex w-full flex-col gap-3">
        <Button
          type="submit"
          className="group w-full"
          disabled={loading}
          loading={loading}
          center
        >
          <SafeHtml content={t("verify_button", "Verify and finish")} />
        </Button>

        <div className="flex items-center justify-between text-body-2">
          <button
            type="button"
            className={`underline ${loading ? "opacity-60 cursor-not-allowed" : ""
              }`}
            onClick={() => {
              if (loading) return;
              resetMessages();
              clearOtp();
              setSignupId("");
              setStep("start");
            }}
            disabled={loading}
          >
            {t("restart_button", "Restart")}
          </button>

          <button type="button" className="underline" onClick={onSwitchToLogin}>
            {tCheckout("switch_to_login", "Sign in")}
          </button>
        </div>
      </div>
    </form>
  );
}

// ---- Main checkout page ------------------------------------------------

export default function CheckoutPageClient({
  lang,
  initialCart = null,
  initialMe,
}: Props & { initialMe: Me | null }) {
  const t = useCheckoutT();
  const { setItemsCount } = useCart();
  const { templateRoutes } = useLayoutData();
  const locale = useMemo(() => resolveLocaleFromLang(lang), [lang]);
  const shopHref = templateRoutes?.["page-shop.php"];

  function baseHeaders(extra?: Record<string, string>) {
    return {
      Accept: "application/json",
      ...(lang ? { "X-Lang": lang } : {}),
      ...(extra ?? {}),
    };
  }

  const topColRef = useRef<HTMLDivElement | null>(null);

  function scrollToTopCol() {
    const el = topColRef.current;
    if (!el) return;
    requestAnimationFrame(() => scrollToEl(el, 96));
  }

  const [me, setMe] = useState<Me | null>(initialMe);
  const [accountView, setAccountView] = useState<"signup" | "login">("signup");
  const [accountRequiredMsg, setAccountRequiredMsg] = useState<string | null>(
    null
  );

  const accountRef = useRef<HTMLDivElement | null>(null);

  function scrollToAccount() {
    const el = accountRef.current;
    if (!el) return;
    requestAnimationFrame(() => scrollToEl(el, 96));
  }

  async function refreshMe(): Promise<Me | null> {
    try {
      const res = await fetch(apiPath("/api/auth/me"), {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
        credentials: "include",
      });

      if (!res.ok) {
        setMe(null);
        return null;
      }

      const json = (await res.json().catch(() => null)) as unknown;

      if (json && typeof json === "object") {
        const rec = json as Record<string, unknown>;

        if (typeof rec.id === "number" && typeof rec.email === "string") {
          const user = json as Me;
          setMe(user);
          return user;
        }
      }

      setMe(null);
      return null;
    } catch {
      return null;
    }
  }

  const initialCartCoerced = useMemo(
    () => coerceCart(initialCart),
    [initialCart]
  );

  const [cart, setCart] = useState<Cart | null>(initialCartCoerced);
  const [isLoading, setIsLoading] = useState<boolean>(!initialCartCoerced);
  const [topError, setTopError] = useState<string | null>(null);

  const items: CartItem[] = useMemo(() => {
    const arr = cart?.items;
    return Array.isArray(arr) ? arr : [];
  }, [cart]);

  const itemsCount = useMemo(() => {
    const direct = cart?.items_count;
    if (direct !== undefined && direct !== null) return toInt(direct, 0);

    let sum = 0;
    for (const it of items) sum += toInt(it?.quantity, 0);
    return sum;
  }, [cart, items]);

  const subtotal = useMemo(
    () =>
      formatCartTotalsMoney(
        cart?.totals,
        cart?.totals?.total_items ?? null,
        locale
      ),
    [cart, locale]
  );

  const total = useMemo(
    () =>
      formatCartTotalsMoney(
        cart?.totals,
        cart?.totals?.total_price ?? cart?.totals?.total ?? null,
        locale
      ),
    [cart, locale]
  );

  const discount = useMemo(
    () =>
      formatCartTotalsMoney(
        cart?.totals,
        cart?.totals?.total_discount ?? null,
        locale
      ),
    [cart, locale]
  );

  const hasDiscount = useMemo(() => {
    const primitive = getMoneyPrimitive(cart?.totals?.total_discount);
    if (primitive === null) return false;
    const n = Number(primitive);
    return Number.isFinite(n) && n > 0;
  }, [cart]);

  const isFreeOrder = useMemo(() => {
    const tot = cart?.totals;
    if (!tot) return false;
    const primitive = getMoneyPrimitive(tot.total_price ?? tot.total);
    if (primitive === null) return false;
    const n = Number(primitive);
    return Number.isFinite(n) && n === 0;
  }, [cart]);

  function syncCart(nextCartUnknown: unknown) {
    const nextCart = coerceCart(nextCartUnknown);
    setCart(nextCart);
    setItemsCount(toInt(nextCart?.items_count, 0));
  }

  async function loadCart() {
    setTopError(null);
    setIsLoading(true);

    try {
      const res = await fetch(apiPath("/api/wc/cart/get"), {
        method: "GET",
        headers: baseHeaders(),
        cache: "no-store",
        credentials: "include",
      });

      if (!res.ok) {
        const loadPrefixTpl = t("error_load_prefix", "Failed to load checkout ({status})");
        const msg = await getNiceErrorMessage(res, loadPrefixTpl);
        throw new Error(msg || loadPrefixTpl.replace("{status}", String(res.status)));
      }

      syncCart((await res.json().catch(() => null)) as unknown);
    } catch (e: unknown) {
      setTopError(
        e instanceof Error ? e.message : t("error_load_failed", "Could not load checkout.")
      );
      scrollToTopCol();
    } finally {
      setIsLoading(false);
    }
  }

  const [placing, setPlacing] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [successOrder, setSuccessOrder] = useState<SuccessOrder | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oid = params.get("oid");
    const key = params.get("key");

    if (!oid) return;

    const clean = new URL(window.location.href);
    clean.searchParams.delete("oid");
    clean.searchParams.delete("key");
    window.history.replaceState({}, "", clean.toString());

    async function fetchOrder() {
      try {
        const res = await fetch(
          apiPath(`/api/wc/order/${encodeURIComponent(oid!)}?key=${encodeURIComponent(key ?? "")}`),
          {
            headers: baseHeaders(),
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const json = (await res.json().catch(() => null)) as unknown;
        if (!json) return;

        const extracted = extractOrderRef(json);

        setCart(null);
        setItemsCount(0);

        fetch(apiPath("/api/wc/cart/clear"), {
          method: "POST",
          headers: baseHeaders(),
          credentials: "include",
          cache: "no-store",
        }).catch(() => { });

        setSuccessOrder({
          ...extracted,
          redirectUrl: null,
        });
      } catch { }
    }

    void fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialCartCoerced) {
      setItemsCount(toInt(initialCartCoerced.items_count, itemsCount));
      return;
    }

    void loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appliedCoupons: Coupon[] = useMemo(() => {
    const c = cart?.coupons;
    return Array.isArray(c) ? c : [];
  }, [cart]);

  async function applyPromo(code: string) {
    setTopError(null);

    const res = await fetch(apiPath("/api/wc/cart/apply-coupon"), {
      method: "POST",
      headers: baseHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ code }),
      cache: "no-store",
      credentials: "include",
    });

    if (!res.ok) {
      const promoApplyTpl = t("promo_apply_prefix", "Could not apply code ({status})");
      const msg = await getNiceErrorMessage(res, promoApplyTpl);
      setTopError(msg || promoApplyTpl.replace("{status}", String(res.status)));
      scrollToTopCol();
      throw new Error(msg);
    }

    const json = (await res.json().catch(() => null)) as unknown;
    if (json) syncCart(json);
  }

  async function removePromo(code: string) {
    setTopError(null);

    const res = await fetch(apiPath("/api/wc/cart/remove-coupon"), {
      method: "POST",
      headers: baseHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ code }),
      cache: "no-store",
      credentials: "include",
    });

    if (!res.ok) {
      const promoRemoveTpl = t("promo_remove_prefix", "Could not remove code ({status})");
      const msg = await getNiceErrorMessage(res, promoRemoveTpl);
      setTopError(msg || promoRemoveTpl.replace("{status}", String(res.status)));
      scrollToTopCol();
      throw new Error(msg);
    }

    const json = (await res.json().catch(() => null)) as unknown;
    if (json) syncCart(json);
  }

  const [billing, setBilling] = useState<AddressForm>(EMPTY_ADDR);
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [address1Error, setAddress1Error] = useState<string | null>(null);
  const [cityError, setCityError] = useState<string | null>(null);
  const [postcodeError, setPostcodeError] = useState<string | null>(null);
  const [countryError, setCountryError] = useState<string | null>(null);

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod>("mbway_ifthen_for_woocommerce");

  const [mbwayPhone, setMbwayPhone] = useState("");
  const [mbwayCountryCode] = useState("PT");
  const [mbwayPhoneError, setMbwayPhoneError] = useState<string | null>(null);

  const firstRef = useRef<HTMLInputElement | null>(null);
  const lastRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const a1Ref = useRef<HTMLInputElement | null>(null);
  const cityRef = useRef<HTMLInputElement | null>(null);
  const postcodeRef = useRef<HTMLInputElement | null>(null);
  const countryRef = useRef<HTMLInputElement | null>(null);

  function resetFieldErrors() {
    setFirstNameError(null);
    setLastNameError(null);
    setEmailError(null);
    setAddress1Error(null);
    setCityError(null);
    setPostcodeError(null);
    setCountryError(null);
    setMbwayPhoneError(null);
  }

  function focusAndScroll(ref: React.RefObject<HTMLInputElement | null>) {
    const el = ref.current;
    if (!el) return;
    el.focus?.();
    requestAnimationFrame(() => scrollToEl(el, 120));
  }

  useEffect(() => {
    if (!me) return;

    setBilling((prev) => ({
      ...prev,
      email: me.email || prev.email,
      first_name: me.billing?.first_name || prev.first_name,
      last_name: me.billing?.last_name || prev.last_name,
      address_1: me.billing?.address_1 || prev.address_1,
      address_2: me.billing?.address_2 || prev.address_2,
      city: me.billing?.city || prev.city,
      state: me.billing?.state || prev.state,
      postcode: me.billing?.postcode || prev.postcode,
      country: me.billing?.country || prev.country,
      phone: me.billing?.phone || prev.phone,
      nif: me.billing?.nif || prev.nif,
    }));
  }, [me]);

  async function onLogout() {
    if (loggingOut) return;

    setLoggingOut(true);

    try {
      await api.post("/api/auth/logout");
    } catch { }

    setLoggingOut(false);
    setMe(null);
  }

  async function onAuthedProceed() {
    const user = await refreshMe();
    if (!user) return;

    setAccountRequiredMsg(null);
    setBilling((b) => ({ ...b, email: user.email }));
  }

  function validateBilling(): boolean {
    resetFieldErrors();

    let firstErrorRef: React.RefObject<HTMLInputElement | null> | null = null;

    function fail(
      setter: (msg: string) => void,
      msg: string,
      ref: React.RefObject<HTMLInputElement | null>
    ) {
      setter(msg);
      if (!firstErrorRef) firstErrorRef = ref;
    }

    if (!billing.first_name.trim()) {
      fail(setFirstNameError, t("error_first_name_required", "Please enter your first name."), firstRef);
    }

    if (!billing.last_name.trim()) {
      fail(setLastNameError, t("error_last_name_required", "Please enter your last name."), lastRef);
    }

    if (!billing.email.trim()) {
      fail(setEmailError, t("error_email_required", "Please enter your email."), emailRef);
    } else if (!looksLikeEmail(billing.email)) {
      fail(setEmailError, t("error_email_invalid", "Please enter a valid email address."), emailRef);
    }

    if (!billing.address_1.trim()) {
      fail(setAddress1Error, t("error_address_required", "Please enter your address."), a1Ref);
    }

    if (!billing.city.trim()) {
      fail(setCityError, t("error_city_required", "Please enter your city."), cityRef);
    }

    if (!billing.postcode.trim()) {
      fail(
        setPostcodeError,
        t("error_postcode_required", "Please enter your postcode."),
        postcodeRef
      );
    }

    if (!billing.country.trim()) {
      fail(setCountryError, t("error_country_required", "Please enter your country."), countryRef);
    }

    if (!isFreeOrder && selectedPaymentMethod === "mbway_ifthen_for_woocommerce") {
      const digits = mbwayPhone.replace(/\D/g, "");

      if (!(digits.length === 9 && digits.startsWith("9"))) {
        setMbwayPhoneError(t("payment_mbway_phone_error", "Enter a valid MB WAY mobile number."));
        if (!firstErrorRef) firstErrorRef = postcodeRef;
      }
    }

    if (firstErrorRef) {
      focusAndScroll(firstErrorRef);
      return false;
    }

    return true;
  }

  async function placeOrder() {
    setTopError(null);

    if (items.length === 0) return;

    let user = me;
    if (!user) user = await refreshMe();

    if (!user) {
      setAccountRequiredMsg(
        t("account_required_message", "Account required — please create an account or sign in to place your order.")
      );
      setAccountView("signup");
      scrollToAccount();
      return;
    }

    if (!validateBilling()) return;

    const snapshotItems: CartItem[] = items.map((it) => ({
      key: it.key,
      id: it.id,
      name: it.name,
      permalink: it.permalink,
      quantity: it.quantity,
      item_data: Array.isArray(it.item_data) ? [...it.item_data] : undefined,
      meta_data: Array.isArray(it.meta_data) ? [...it.meta_data] : undefined,
      variation: it.variation ? { ...it.variation } : undefined,
      totals: it.totals ? { ...it.totals } : undefined,
      prices: it.prices ? { ...it.prices } : undefined,
    }));

    const snapshotSubtotal = subtotal ?? null;
    const snapshotTotal = total ?? null;

    setPlacing(true);

    try {
      type PaymentDataEntry = { key: string; value: unknown };

      const effectivePaymentMethod: PaymentMethod = isFreeOrder
        ? "free_ticket"
        : selectedPaymentMethod;

      let paymentData: PaymentDataEntry[] = [];

      if (!isFreeOrder && effectivePaymentMethod === "mbway_ifthen_for_woocommerce") {
        paymentData = [
          {
            key: "mbway_ifthen_for_woocommerce_phone",
            value: mbwayPhone.replace(/\D/g, ""),
          },
          {
            key: "mbway_ifthen_for_woocommerce_country_code",
            value: mbwayCountryCode,
          },
        ];
      }

      const cleanBilling = {
        first_name: billing.first_name.trim(),
        last_name: billing.last_name.trim(),
        email: user.email,
        phone: billing.phone.trim(),
        address_1: billing.address_1.trim(),
        address_2: billing.address_2.trim(),
        city: billing.city.trim(),
        state: billing.state.trim(),
        postcode: billing.postcode.trim(),
        country: billing.country.trim(),
        nif: billing.nif.trim(),
      };

      const payload = {
        billing_address: cleanBilling,
        shipping_address: {
          first_name: cleanBilling.first_name,
          last_name: cleanBilling.last_name,
          address_1: cleanBilling.address_1,
          address_2: cleanBilling.address_2,
          city: cleanBilling.city,
          state: cleanBilling.state,
          postcode: cleanBilling.postcode,
          country: cleanBilling.country,
        },
        payment_method: effectivePaymentMethod,
        payment_data: paymentData,
        // Store API extension slot — read by bright-order-nif.php mu-plugin
        // to stamp _billing_nif on the order. Additive: does not affect the
        // existing billing_address / user-meta flow.
        extensions: {
          bright: {
            nif: billing.nif.trim(),
          },
        },
      };

      fetch(apiPath("/api/auth/me/update"), {
        method: "POST",
        headers: baseHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ billing: cleanBilling }),
        cache: "no-store",
        credentials: "include",
      }).catch(() => { });

      const res = await fetch(apiPath("/api/wc/checkout"), {
        method: "POST",
        headers: baseHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
        cache: "no-store",
        credentials: "include",
      });

      if (res.status === 401 || res.status === 403) {
        setMe(null);
        setAccountView("login");
        setAccountRequiredMsg(
          t("session_expired_message", "Your session has expired — please sign in again.")
        );
        scrollToAccount();
        setPlacing(false);
        return;
      }

      if (!res.ok) {
        const checkoutTpl = t("error_checkout_prefix", "Checkout failed ({status})");
        const msg = await getNiceErrorMessage(res, checkoutTpl);
        setTopError(msg || checkoutTpl.replace("{status}", String(res.status)));
        scrollToTopCol();
        setPlacing(false);
        return;
      }

      const json = (await res.json().catch(() => null)) as unknown;
      const extracted = extractOrderRef(json);

      const nextOrder: SuccessOrder = {
        ...extracted,
        email: extracted.email ?? user.email ?? billing.email ?? null,
        total: extracted.total ?? snapshotTotal ?? null,
        subtotal: snapshotSubtotal,
        items: snapshotItems,
      };

      setCart(null);
      setItemsCount(0);

      fetch(apiPath("/api/wc/cart/clear"), {
        method: "POST",
        headers: baseHeaders(),
        credentials: "include",
        cache: "no-store",
      }).catch(() => { });

      setPlacing(false);
      setSuccessOrder(nextOrder);
    } catch (e: unknown) {
      setTopError(
        e instanceof Error ? e.message : t("error_place_failed", "Could not place order.")
      );
      scrollToTopCol();
      setPlacing(false);
    }
  }

  if (successOrder) {
    return <CheckoutSuccessView lang={lang} successOrder={successOrder} />;
  }

  const itemsCountLabel =
    itemsCount === 1
      ? t("items_count_singular", "item")
      : t("items_count_plural", "items");

  return (
    <Section className="overflow-visible">
      <Wrapper>
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="text-title-1 font-semibold">{t("heading", "Checkout")}</h1>
          <div className="text-sm opacity-80">
            {itemsCount} {itemsCountLabel}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="pt-2" ref={topColRef} />

            {topError ? (
              <CheckoutErrorBanner title={t("error_generic_title", "Something went wrong")} message={topError} />
            ) : null}

            {isLoading && items.length === 0 ? (
              <div className="rounded-2xl border p-6 text-sm opacity-80">
                {t("loading_message", "Loading checkout…")}
              </div>
            ) : null}

            {!isLoading && !topError && !placing && items.length === 0 ? (
              <div className="border rounded-2xl p-8 text-center">
                <p className="text-lg font-medium">{t("empty_message", "Your cart is empty")}</p>
                <Link
                  href={shopHref || ""}
                  className="inline-block mt-4 border px-6 py-3 rounded-xl"
                >
                  {t("view_events_button", "View events")}
                </Link>
              </div>
            ) : null}

            {!isLoading && items.length > 0 ? (
              <div className="flex flex-col gap-10">
                <div ref={accountRef}>
                  <div className="font-bold">{t("account_heading", "Account")}</div>
                  <hr className="my-4" />

                  {accountRequiredMsg ? (
                    <CheckoutErrorBanner
                      title={t("account_required_title", "Account required")}
                      message={accountRequiredMsg}
                    />
                  ) : null}

                  {me ? (
                    <div className="rounded-2xl border p-4 text-sm flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-medium">{t("logged_in_label", "Signed in")}</div>
                        <div className="mt-1 opacity-80 truncate">{me.email}</div>
                      </div>

                      <Button
                        type="button"
                        center
                        bgStyle="bg-fuji-grey hover:bg-fuji-grey/80 border-fuji-grey"
                        onClick={onLogout}
                        disabled={loggingOut}
                        loading={loggingOut}
                      >
                        {t("logout_button", "Sign out")}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-end mb-6">
                        <div className="flex items-center gap-2 text-sm opacity-80">
                          <span>
                            {accountView === "signup"
                              ? t("have_account_prompt", "Already have an account?")
                              : t("need_account_prompt", "Need an account?")}
                          </span>

                          <Button
                            type="button"
                            className="text-body"
                            center
                            bgStyle="bg-fuji-grey hover:bg-fuji-grey/80 border-fuji-grey"
                            onClick={() =>
                              setAccountView((v) =>
                                v === "signup" ? "login" : "signup"
                              )
                            }
                          >
                            {accountView === "signup"
                              ? t("switch_to_login", "Sign in")
                              : t("switch_to_signup", "Register now")}
                          </Button>
                        </div>
                      </div>

                      {accountView === "login" ? (
                        <InlineLogin
                          prefillEmail={billing.email}
                          onAuthed={onAuthedProceed}
                        />
                      ) : (
                        <InlineSignup
                          prefillEmail={billing.email}
                          onAuthed={onAuthedProceed}
                          onSwitchToLogin={() => setAccountView("login")}
                          lang={lang}
                        />
                      )}
                    </>
                  )}
                </div>

                <div>
                  <div className="font-bold">{t("billing_heading", "Billing details")}</div>
                  <hr className="my-4" />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="w-full min-w-0">
                      <Input
                        id="bill-first"
                        ref={firstRef}
                        label={t("billing_first_name", "First name")}
                        value={billing.first_name}
                        onChange={(e) => {
                          setFirstNameError(null);
                          setBilling((b) => ({
                            ...b,
                            first_name: e.target.value,
                          }));
                        }}
                        aria-invalid={!!firstNameError || undefined}
                        aria-describedby={
                          firstNameError ? "bill-first-error" : undefined
                        }
                        invalid={!!firstNameError}
                        required
                      />

                      {firstNameError ? (
                        <small
                          id="bill-first-error"
                          role="alert"
                          className="mt-1 block text-body-3 text-fuji-red"
                        >
                          {firstNameError}
                        </small>
                      ) : null}
                    </div>

                    <div className="w-full min-w-0">
                      <Input
                        id="bill-last"
                        ref={lastRef}
                        label={t("billing_last_name", "Last name")}
                        value={billing.last_name}
                        onChange={(e) => {
                          setLastNameError(null);
                          setBilling((b) => ({
                            ...b,
                            last_name: e.target.value,
                          }));
                        }}
                        aria-invalid={!!lastNameError || undefined}
                        aria-describedby={
                          lastNameError ? "bill-last-error" : undefined
                        }
                        invalid={!!lastNameError}
                        required
                      />

                      {lastNameError ? (
                        <small
                          id="bill-last-error"
                          role="alert"
                          className="mt-1 block text-body-3 text-fuji-red"
                        >
                          {lastNameError}
                        </small>
                      ) : null}
                    </div>

                    <div className="sm:col-span-2 w-full min-w-0">
                      <Input
                        id="bill-email"
                        ref={emailRef}
                        label={t("billing_email", "Email")}
                        type="email"
                        value={billing.email}
                        onChange={(e) => {
                          setEmailError(null);
                          setBilling((b) => ({
                            ...b,
                            email: e.target.value,
                          }));
                        }}
                        autoComplete="email"
                        aria-invalid={!!emailError || undefined}
                        aria-describedby={
                          emailError ? "bill-email-error" : undefined
                        }
                        invalid={!!emailError}
                        required
                      />

                      {emailError ? (
                        <small
                          id="bill-email-error"
                          role="alert"
                          className="mt-1 block text-body-3 text-fuji-red"
                        >
                          {emailError}
                        </small>
                      ) : null}
                    </div>

                    <div className="sm:col-span-2 w-full min-w-0">
                      <Input
                        id="bill-a1"
                        ref={a1Ref}
                        label={t("billing_address_1", "Address line 1")}
                        value={billing.address_1}
                        onChange={(e) => {
                          setAddress1Error(null);
                          setBilling((b) => ({
                            ...b,
                            address_1: e.target.value,
                          }));
                        }}
                        aria-invalid={!!address1Error || undefined}
                        aria-describedby={
                          address1Error ? "bill-a1-error" : undefined
                        }
                        invalid={!!address1Error}
                        required
                      />

                      {address1Error ? (
                        <small
                          id="bill-a1-error"
                          role="alert"
                          className="mt-1 block text-body-3 text-fuji-red"
                        >
                          {address1Error}
                        </small>
                      ) : null}
                    </div>

                    <div className="sm:col-span-2 w-full min-w-0">
                      <Input
                        id="bill-a2"
                        label={t("billing_address_2", "Address line 2")}
                        value={billing.address_2}
                        onChange={(e) =>
                          setBilling((b) => ({
                            ...b,
                            address_2: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="w-full min-w-0">
                      <Input
                        id="bill-city"
                        ref={cityRef}
                        label={t("billing_city", "City")}
                        value={billing.city}
                        onChange={(e) => {
                          setCityError(null);
                          setBilling((b) => ({
                            ...b,
                            city: e.target.value,
                          }));
                        }}
                        aria-invalid={!!cityError || undefined}
                        aria-describedby={
                          cityError ? "bill-city-error" : undefined
                        }
                        invalid={!!cityError}
                        required
                      />

                      {cityError ? (
                        <small
                          id="bill-city-error"
                          role="alert"
                          className="mt-1 block text-body-3 text-fuji-red"
                        >
                          {cityError}
                        </small>
                      ) : null}
                    </div>

                    <div className="w-full min-w-0">
                      <Input
                        id="bill-postcode"
                        ref={postcodeRef}
                        label={t("billing_postcode", "Postcode")}
                        value={billing.postcode}
                        onChange={(e) => {
                          setPostcodeError(null);
                          setBilling((b) => ({
                            ...b,
                            postcode: e.target.value,
                          }));
                        }}
                        aria-invalid={!!postcodeError || undefined}
                        aria-describedby={
                          postcodeError ? "bill-postcode-error" : undefined
                        }
                        invalid={!!postcodeError}
                        required
                      />

                      {postcodeError ? (
                        <small
                          id="bill-postcode-error"
                          role="alert"
                          className="mt-1 block text-body-3 text-fuji-red"
                        >
                          {postcodeError}
                        </small>
                      ) : null}
                    </div>

                    <div className="w-full min-w-0">
                      <CountrySelect
                        label={t("billing_country", "Country")}
                        value={billing.country}
                        onChange={(v) => {
                          setCountryError(null);
                          setBilling((b) => ({ ...b, country: v }));
                        }}
                        invalid={!!countryError}
                        lang={lang}
                        required
                      />

                      {countryError ? (
                        <small
                          role="alert"
                          className="mt-1 block text-body-3 text-fuji-red"
                        >
                          {countryError}
                        </small>
                      ) : null}
                    </div>

                    <div className="w-full min-w-0">
                      <Input
                        id="bill-nif"
                        label={t("billing_nif", "Tax ID")}
                        value={billing.nif}
                        onChange={(e) =>
                          setBilling((b) => ({ ...b, nif: e.target.value }))
                        }
                        autoComplete="off"
                      />
                    </div>

                    <div className="w-full min-w-0">
                      <Input
                        id="bill-phone"
                        label={t("billing_phone", "Phone")}
                        value={billing.phone}
                        onChange={(e) =>
                          setBilling((b) => ({
                            ...b,
                            phone: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <CheckoutSidebar
            lang={lang}
            items={items}
            subtotal={subtotal}
            total={total}
            discount={hasDiscount ? discount : null}
            meEmail={me?.email ?? null}
            placing={placing}
            onPlaceOrder={placeOrder}
            appliedCoupons={appliedCoupons}
            onApplyPromo={applyPromo}
            onRemovePromo={removePromo}
            selectedPaymentMethod={selectedPaymentMethod}
            onChangePaymentMethod={setSelectedPaymentMethod}
            mbwayPhone={mbwayPhone}
            onChangeMbwayPhone={setMbwayPhone}
            mbwayPhoneError={mbwayPhoneError}
            onClearMbwayPhoneError={() => setMbwayPhoneError(null)}
            isFreeOrder={isFreeOrder}
          />
        </div>
      </Wrapper>
    </Section>
  );
}